import type { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { resolveHttpError, withRequestStatus } from '@indutrust/shared-util';
import { mapResponse } from '@ngrx/operators';
import { signalStore } from '@ngrx/signals';
import {
  Events,
  on,
  withEventHandlers,
  withReducer,
} from '@ngrx/signals/events';
import { switchMap, tap } from 'rxjs';
import { FactoryActionsApiClient } from './factory-actions-api-client.service';
import { factoryLogoEvents } from './factory-logo.events';
import { FactoryStore } from './factory.store';

/**
 * Workflow store for the factory logo multipart upload.
 *
 * Exposes {@link withRequestStatus | request-status} signals (`isLoading`,
 * `isSuccess`, `error`). On success, calls {@link FactoryStore.loadById} so
 * the scoped factory store refreshes the hero image without feature-layer HTTP.
 *
 * @example
 * const logoStore = inject(FactoryLogoStore);
 * const dispatch = injectDispatch(factoryLogoEvents);
 * dispatch.updateLogoRequested({ factoryId: 7, file });
 */
export const FactoryLogoStore = signalStore(
  withRequestStatus(),
  withReducer(
    on(factoryLogoEvents.updateLogoRequested, () => ({
      requestStatus: 'loading',
    })),
    on(factoryLogoEvents.updateLogoSucceeded, () => ({
      requestStatus: 'success',
    })),
    on(factoryLogoEvents.updateLogoFailed, ({ payload }) => ({
      requestStatus: { error: payload.error },
    }))
  ),
  withEventHandlers(
    (
      _,
      events = inject(Events),
      actions = inject(FactoryActionsApiClient),
      factoryStore = inject(FactoryStore)
    ) => ({
      updateLogo$: events.on(factoryLogoEvents.updateLogoRequested).pipe(
        switchMap(({ payload }) =>
          actions.updateLogo(payload.factoryId, payload.file).pipe(
            mapResponse({
              next: (factory) =>
                factoryLogoEvents.updateLogoSucceeded({
                  factory,
                  factoryId: payload.factoryId,
                }),
              error: (error: HttpErrorResponse) =>
                factoryLogoEvents.updateLogoFailed({
                  error: resolveHttpError(error).code,
                }),
            })
          )
        )
      ),
      refreshEntityOnLogoSuccess$: events
        .on(factoryLogoEvents.updateLogoSucceeded)
        .pipe(
          tap(({ payload }) => {
            factoryStore.loadById(payload.factoryId);
          })
        ),
    })
  )
);
